// Wallet detail — balance dashboard + manual adjustment (deposit /
// withdraw) + freeze/unfreeze + transaction / deposit / withdrawal
// history. Mirrors users.$id.tsx in shape; the wallet directory
// (treasury.tsx) routes here on row click so freeze/adjust no longer
// clutter the list view.
//
// Manual adjustment posts an instant `ADJUSTMENT` to the wallet:
// deposit credits available, withdraw debits it. Both share the
// WALLET_CREDIT permission and the same audit envelope as the user-side
// deposit/withdraw — the operator equivalent of the client wallet's
// add-funds / cash-out actions. (Distinct from the deposit/withdrawal
// *lifecycle* rows below, which the provider webhooks drive.)
//
// Each history panel paginates independently — a single wallet can
// have thousands of transactions but only a handful of deposits, so
// shared pagination state would force operators to scroll past
// thousands of rows to reach a recent failed deposit.
//
// PENDING deposits / withdrawals expose a kebab menu with admin
// verbs (mark completed / failed / cancel). The server tags the
// resulting `reason` field with `[admin:<id>]` so the row carries
// at-a-glance attribution back to the acting operator on subsequent
// reads.
//
// EXCEPTION — provider CRYPTO: those ledger rows are driven by the
// crypto rail's own state machine (chainnode confirmations → projector
// credit; withdrawal executor sign → broadcast → receipt). Manually
// completing/failing/cancelling them here would fight that lifecycle
// (worst case: cancel the hold while the executor is broadcasting =
// funds leave on-chain AND the SPARK is refunded). Crypto rows link to
// Wallet Ops — the actual wallet procedures — instead of the kebab.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  adminCancelWithdrawal,
  adminCompleteDeposit,
  adminCompleteWithdrawal,
  adminFailDeposit,
  adminFailWithdrawal,
  fetchUserDetail,
  fetchWallet,
  fetchWalletBalances,
  fetchWalletDeposits,
  fetchWalletTransactions,
  fetchWalletReservations,
  fetchWalletWithdrawals,
  freezeWallet,
  keys,
  unfreezeWallet,
} from "@/lib/queries";
import { campaignCurrency, isCurrencyCode, type CurrencyCode } from "@/lib/currency";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { cn } from "@/lib/cn";
import { CurrencyChip } from "@/components/currency/columns";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { Pagination } from "@/components/ui/Pagination";
import { Skeleton } from "@/components/ui/Skeleton";
import { ManualAdjustmentCard } from "@/components/treasury/ManualAdjustmentCard";
import { fmtRelative, statusLabel } from "@/lib/format";
import { SparkAmount } from "@/components/ui/SparkAmount";
import { useCurrencyPresentation } from "@/components/currency/CurrencyPresentationProvider";
import { formatCurrencyText } from "@/currency";
import type { UserProfile, WalletReservation } from "@/lib/types";

type PendingAction = "freeze" | "unfreeze";

// Each history panel paginates at this slice size. Kept small so the
// card stays a fixed compact height instead of growing past the
// neighbouring widgets when a wallet has lots of rows. Operators reach
// older rows through the pagination strip below the table.
const HISTORY_PAGE = 10;

// Verb of an admin-initiated deposit/withdrawal transition. Drives the
// modal copy + which mutation runs when the operator confirms.
type RowVerb =
  | "deposit:complete"
  | "deposit:fail"
  | "withdrawal:complete"
  | "withdrawal:cancel"
  | "withdrawal:fail";

interface RowAction {
  verb: RowVerb;
  id: string;
}

export const Route = createFileRoute("/_app/treasury/$id")({
  // A user holds ONE WALLET PER CURRENCY since ledger 0025, and the list
  // above is already one row per (user, currency) — but all three rows
  // linked here without saying which, so this page always showed GOLD.
  // Optional: absent means GOLD, the same default ledger applies.
  validateSearch: (search: Record<string, unknown>): { currency?: CurrencyCode } =>
    typeof search.currency === "string" && isCurrencyCode(search.currency)
      ? { currency: search.currency }
      : {},
  component: WalletDetailPage,
});

function WalletDetailPage() {
  const { id } = Route.useParams();
  const currency = campaignCurrency(Route.useSearch().currency);
  // The reserved-breakdown total is prose, not a <CurrencyAmount>, so it
  // needs the presentation explicitly. It used to run through
  // formatSparkText, which suffixed a hardcoded "GOLD" — wrong the moment
  // the wallet on screen is RED or GREEN, and stale the moment an operator
  // renames the currency (SM-491).
  const presentation = useCurrencyPresentation(currency);
  const qc = useQueryClient();

  const wallet = useQuery({
    queryKey: keys.walletDetail(id, currency),
    queryFn: () => fetchWallet(id, currency),
  });
  // Every currency this user holds. The card below shows ONE wallet; this
  // answers the question that page cannot — what else is there — so a
  // currency is not missed just because nobody navigated to it.
  const balances = useQuery({
    queryKey: keys.walletBalances(id),
    queryFn: () => fetchWalletBalances(id),
  });

  // Reuse the composite user-detail endpoint for profile context
  // (email + display name + roles) — the same endpoint backs the
  // user-detail page, so we get cache reuse for free.
  const user = useQuery({
    queryKey: keys.userDetail(id),
    queryFn: () => fetchUserDetail(id),
  });
  const profile: UserProfile | undefined = user.data?.profile;

  // Per-panel offsets. Each panel paginates on its own clock.
  const [txnPage, setTxnPage] = useState(0);
  const [depPage, setDepPage] = useState(0);
  const [wdPage, setWdPage] = useState(0);

  const transactions = useQuery({
    queryKey: keys.walletTxns(id, HISTORY_PAGE, txnPage * HISTORY_PAGE),
    queryFn: () =>
      fetchWalletTransactions(id, {
        limit: HISTORY_PAGE,
        offset: txnPage * HISTORY_PAGE,
      }),
  });
  const deposits = useQuery({
    queryKey: keys.walletDeposits(id, HISTORY_PAGE, depPage * HISTORY_PAGE),
    queryFn: () =>
      fetchWalletDeposits(id, {
        limit: HISTORY_PAGE,
        offset: depPage * HISTORY_PAGE,
      }),
  });
  const withdrawals = useQuery({
    queryKey: keys.walletWithdrawals(id, HISTORY_PAGE, wdPage * HISTORY_PAGE),
    queryFn: () =>
      fetchWalletWithdrawals(id, {
        limit: HISTORY_PAGE,
        offset: wdPage * HISTORY_PAGE,
      }),
  });
  // Reservations breakdown — drives the "Reserved" card under the
  // wallet summary. 5s refetch so operators can watch the
  // ReserveReleaseSweeper drain the queue post-Bug-A deploy without
  // mashing reload. Cap at 200 — the ledger admin endpoint is also
  // capped at 200 today and items.length === RESERVATIONS_CAP becomes
  // the "saturated, narrow your search" hint.
  const RESERVATIONS_CAP = 200;
  const reservations = useQuery({
    queryKey: keys.walletReservations(id, RESERVATIONS_CAP),
    queryFn: () => fetchWalletReservations(id, { limit: RESERVATIONS_CAP }),
    refetchInterval: 5_000,
  });

  // --- mutations ---

  const [pending, setPending] = useState<PendingAction | null>(null);

  // Row-level action state. `action` is non-null while the confirm
  // modal is open; `error` surfaces inline on failure.
  const [rowAction, setRowAction] = useState<RowAction | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const invalidate = () => {
    // Every currency of this user: a freeze or adjustment can move more
    // than the wallet currently on screen.
    qc.invalidateQueries({ queryKey: keys.walletDetailAll(id) });
    qc.invalidateQueries({ queryKey: keys.treasury });
    qc.invalidateQueries({ queryKey: keys.walletTxns(id, HISTORY_PAGE, txnPage * HISTORY_PAGE) });
    qc.invalidateQueries({ queryKey: keys.walletDeposits(id, HISTORY_PAGE, depPage * HISTORY_PAGE) });
    qc.invalidateQueries({ queryKey: keys.walletWithdrawals(id, HISTORY_PAGE, wdPage * HISTORY_PAGE) });
  };

  const freeze = useMutation({
    mutationFn: (reason: string) => freezeWallet(id, reason),
    onSuccess: () => {
      setPending(null);
      invalidate();
    },
    meta: { successMessage: "Wallet frozen." },
  });
  const unfreeze = useMutation({
    mutationFn: (reason: string) => unfreezeWallet(id, reason),
    onSuccess: () => {
      setPending(null);
      invalidate();
    },
    meta: { successMessage: "Wallet unfrozen." },
  });
  const rowMutation = useMutation({
    mutationFn: async (input: { action: RowAction; reason: string }) => {
      const { action, reason } = input;
      switch (action.verb) {
        case "deposit:complete":
          return adminCompleteDeposit(action.id, reason);
        case "deposit:fail":
          return adminFailDeposit(action.id, reason);
        case "withdrawal:complete":
          return adminCompleteWithdrawal(action.id, reason);
        case "withdrawal:cancel":
          return adminCancelWithdrawal(action.id, reason);
        case "withdrawal:fail":
          return adminFailWithdrawal(action.id, reason);
      }
    },
    onSuccess: () => {
      setRowAction(null);
      setRowError(null);
      invalidate();
    },
    onError: (e: Error) => setRowError(e.message),
    meta: { successMessage: "Transaction updated." },
  });

  const closeModal = () => {
    freeze.reset();
    unfreeze.reset();
    setPending(null);
  };
  const closeRowModal = () => {
    rowMutation.reset();
    setRowAction(null);
    setRowError(null);
  };

  const status = wallet.data?.status;
  const isActive = status === "ACTIVE";

  return (
    <div className="space-y-6">
      <div>
        <Link to="/treasury" className="text-2xs text-muted hover:text-fg">← back to treasury</Link>
        <h1 className="mt-1 font-display text-2xl tracking-tight">
          {profile?.email ?? profile?.display_name ?? id}
        </h1>
        <p className="font-mono text-2xs text-muted">{id}</p>
      </div>

      {wallet.isLoading && <Skeleton className="h-32" />}
      {/* An error used to render NOTHING here — no branch at all — so a
          failing read silently collapsed the balance card, the adjustment
          card and every history panel, leaving just the heading. That is
          the mechanism that hid SM-472, where this endpoint 400'd for
          every user on every environment and simply looked empty. */}
      {wallet.isError && <ErrorPanel error={wallet.error} reset={() => wallet.refetch()} />}
      {balances.data && balances.data.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {balances.data.map((b) => {
            const active = campaignCurrency(b.currency) === currency;
            return (
              <Link
                key={b.currency}
                to="/treasury/$id"
                params={{ id }}
                search={{ currency: campaignCurrency(b.currency) }}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-2xs transition-colors",
                  active
                    ? "border-accent/60 bg-accent/10 text-fg"
                    : "border-border text-muted hover:border-accent/40 hover:text-fg",
                )}
                aria-current={active ? "page" : undefined}
              >
                <CurrencyChip currency={b.currency} />
                <CurrencyAmount currency={b.currency} value={b.available} hideIcon />
              </Link>
            );
          })}
        </div>
      )}
      {wallet.data && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader className="border-b border-border">
              <CardTitle>Wallet</CardTitle>
              <Badge tone={isActive ? "success" : "danger"}>{statusLabel(status)}</Badge>
            </CardHeader>
            <CardBody className="space-y-2 pt-5">
              <Field k="Currency" v={<CurrencyChip currency={wallet.data.currency} />} />
              <Field k="Available" v={<span className="num font-medium"><CurrencyAmount currency={wallet.data.currency} value={wallet.data.available} /></span>} />
              <Field k="Reserved" v={<span className="num font-medium"><CurrencyAmount currency={wallet.data.currency} value={wallet.data.reserved} tone="warning" /></span>} />
              <Field k="Pending W/D" v={<CurrencyAmount currency={wallet.data.currency} value={wallet.data.pending_withdrawal} />} />
              <Field k="Updated" v={fmtRelative(wallet.data.updated_at)} />
              <div className="flex items-center gap-1 pt-3">
                {isActive ? (
                  <Button size="sm" variant="ghost" className="text-danger" onClick={() => setPending("freeze")}>
                    Freeze wallet
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => setPending("unfreeze")}>
                    Unfreeze wallet
                  </Button>
                )}
                {/* Deep-link into the transfer page with this wallet
                    pre-selected as the sender, in the currency being
                    looked at — the operator arrived here through the
                    balances strip, so carrying the currency across is
                    the difference between "transfer THIS" and "transfer
                    whatever the form defaults to" (SM-492). */}
                <Link to="/treasury/transfer" search={{ from: id, currency }}>
                  <Button size="sm" variant="ghost">
                    Transfer credit
                  </Button>
                </Link>
              </div>
            </CardBody>
          </Card>

          <ManualAdjustmentCard
            userId={id}
            available={wallet.data.available}
            isActive={isActive}
            status={status}
            onAdjusted={invalidate}
          />

          <Card className="lg:col-span-2">
            <CardHeader className="border-b border-border">
              <CardTitle>Reserved breakdown</CardTitle>
              <span className="text-2xs text-muted">
                {reservations.data?.items.length ?? 0}
                {reservations.data && reservations.data.items.length >= RESERVATIONS_CAP
                  ? "+ (capped)"
                  : ""}
                {reservations.data && reservations.data.items.length > 0
                  ? ` · ${formatCurrencyText(
                      presentation,
                      reservations.data.items.reduce(
                        (s, r) => s + Number(r.remaining || 0),
                        0,
                      ),
                    )} locked`
                  : ""}
              </span>
            </CardHeader>
            <CardBody className="p-0">
              <ReservationsTable
                rows={reservations.data?.items ?? []}
                loading={reservations.isLoading}
              />
            </CardBody>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader className="border-b border-border">
              <CardTitle>Transactions</CardTitle>
              <span className="text-2xs text-muted">{transactions.data?.page.total ?? 0}</span>
            </CardHeader>
            <CardBody className="p-0">
              <TransactionTable rows={transactions.data?.items ?? []} loading={transactions.isLoading} />
              {transactions.data && transactions.data.page.total > HISTORY_PAGE && (
                <Pagination
                  page={transactions.data.page}
                  currentPage={txnPage}
                  onPageChange={setTxnPage}
                  itemLabel="transactions"
                />
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="border-b border-border">
              <CardTitle>Deposits</CardTitle>
              <span className="text-2xs text-muted">{deposits.data?.page.total ?? 0}</span>
            </CardHeader>
            <CardBody className="p-0">
              <DepositTable
                rows={deposits.data?.items ?? []}
                loading={deposits.isLoading}
                onAction={(verb, id) => setRowAction({ verb, id })}
              />
              {deposits.data && deposits.data.page.total > HISTORY_PAGE && (
                <Pagination
                  page={deposits.data.page}
                  currentPage={depPage}
                  onPageChange={setDepPage}
                  itemLabel="deposits"
                />
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="border-b border-border">
              <CardTitle>Withdrawals</CardTitle>
              <span className="text-2xs text-muted">{withdrawals.data?.page.total ?? 0}</span>
            </CardHeader>
            <CardBody className="p-0">
              <WithdrawalTable
                rows={withdrawals.data?.items ?? []}
                loading={withdrawals.isLoading}
                onAction={(verb, id) => setRowAction({ verb, id })}
              />
              {withdrawals.data && withdrawals.data.page.total > HISTORY_PAGE && (
                <Pagination
                  page={withdrawals.data.page}
                  currentPage={wdPage}
                  onPageChange={setWdPage}
                  itemLabel="withdrawals"
                />
              )}
            </CardBody>
          </Card>
        </div>
      )}

      <ConfirmModal
        open={pending === "freeze"}
        tone="danger"
        title="Freeze this wallet?"
        description={
          <>
            <p>
              Blocks all deposits, withdrawals, and order reservations for
              {" "}<span className="font-mono text-2xs text-t-2">{id}</span>.
              Existing reserved balances stay locked until the wallet is unfrozen or the underlying orders settle.
            </p>
            <p className="mt-2 text-2xs">Required reason lands in the audit log alongside the operator id.</p>
          </>
        }
        confirmLabel="Freeze wallet"
        cancelLabel="Cancel"
        reasonLabel="Reason (audit log)"
        reasonRequired
        loading={freeze.isPending}
        error={freeze.isError ? (freeze.error as Error).message : null}
        onConfirm={(r) => freeze.mutate(r)}
        onCancel={closeModal}
      />

      <ConfirmModal
        open={pending === "unfreeze"}
        tone="primary"
        title="Unfreeze this wallet?"
        description={
          <p>
            Restores deposits, withdrawals, and order placement for
            {" "}<span className="font-mono text-2xs text-t-2">{id}</span>.
          </p>
        }
        confirmLabel="Unfreeze wallet"
        cancelLabel="Cancel"
        reasonLabel="Reason (audit log)"
        reasonRequired
        loading={unfreeze.isPending}
        error={unfreeze.isError ? (unfreeze.error as Error).message : null}
        onConfirm={(r) => unfreeze.mutate(r)}
        onCancel={closeModal}
      />

      {rowAction && (
        <ConfirmModal
          open
          tone={rowVerbTone(rowAction.verb)}
          title={rowVerbTitle(rowAction.verb)}
          description={
            <p className="text-2xs">
              Targets <span className="font-mono text-t-2">{rowAction.id}</span>. The reason
              you supply is recorded against the row with an
              {" "}<code className="rounded bg-surface-2 px-1 text-2xs">[admin:&lt;id&gt;]</code>
              {" "}prefix so subsequent readers can tell this was an operator action.
            </p>
          }
          confirmLabel={rowVerbConfirm(rowAction.verb)}
          cancelLabel="Cancel"
          reasonLabel="Reason (audit log)"
          reasonRequired={rowVerbReasonRequired(rowAction.verb)}
          loading={rowMutation.isPending}
          error={rowError}
          onConfirm={(reason) => rowMutation.mutate({ action: rowAction, reason })}
          onCancel={closeRowModal}
        />
      )}
    </div>
  );
}

function Field({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 text-xs">
      <span className="text-muted">{k}</span>
      <span className="text-fg">{v}</span>
    </div>
  );
}

function ReservationsTable({
  rows,
  loading,
}: {
  rows: WalletReservation[];
  loading: boolean;
}) {
  if (loading)
    return (
      <div className="space-y-2 p-5">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-6" />
        ))}
      </div>
    );
  if (rows.length === 0)
    return (
      <p className="px-5 py-6 text-2xs text-muted">
        none — every order's reservation has been debited or released
      </p>
    );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-2xs">
        <thead className="border-b border-border bg-surface-2 text-muted">
          <tr>
            <th className="px-3 py-2 text-left">Opened</th>
            <th className="px-3 py-2 text-left">Order</th>
            <th className="px-3 py-2 text-left">Market</th>
            <th className="px-3 py-2 text-right">Amount</th>
            <th className="px-3 py-2 text-right">Remaining</th>
            <th className="px-3 py-2 text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            // Highlight partially-debited rows — those are the
            // "filled as maker, taker-fee buffer left HELD" candidates
            // the ReserveReleaseSweeper picks up. amount > remaining
            // means at least one fill has landed.
            const partial = Number(r.remaining) < Number(r.amount);
            return (
              <tr
                key={r.reservation_id}
                className="border-b border-border/60 last:border-0"
              >
                <td className="px-3 py-1.5 text-muted">
                  {fmtRelative(r.created_at)}
                </td>
                <td className="px-3 py-1.5 font-mono text-2xs text-muted">
                  {r.order_id}
                </td>
                <td className="px-3 py-1.5 font-mono text-2xs">
                  {r.market_id}
                </td>
                <td className="px-3 py-1.5 text-right font-mono">
                  <SparkAmount value={r.amount} />
                </td>
                <td className="px-3 py-1.5 text-right font-mono">
                  <SparkAmount value={r.remaining} tone={partial ? "warning" : "default"} />
                </td>
                <td className="px-3 py-1.5">
                  <Badge tone={partial ? "warning" : "muted"}>
                    {r.status}
                    {partial ? " · partial" : ""}
                  </Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TransactionTable({ rows, loading }: { rows: any[]; loading: boolean }) {
  if (loading) return <div className="space-y-2 p-5">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-6" />)}</div>;
  if (rows.length === 0) return <p className="px-5 py-6 text-2xs text-muted">none</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-2xs">
        <thead className="border-b border-border bg-surface-2 text-muted">
          <tr>
            <th className="px-3 py-2 text-left">When</th>
            <th className="px-3 py-2 text-left">Kind</th>
            <th className="px-3 py-2 text-right">Display amount</th>
            <th className="px-3 py-2 text-left">Reason</th>
            <th className="px-3 py-2 text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.transaction_id} className="border-b border-border/60 last:border-0">
              <td className="px-3 py-1.5 text-muted">{fmtRelative(t.created_at)}</td>
              <td className="px-3 py-1.5"><Badge tone="muted">{t.kind}</Badge></td>
              <td className={"px-3 py-1.5 text-right font-mono " + amountTone(t.display_amount)}>
                <SparkAmount value={t.display_amount} sign />
              </td>
              <td className="px-3 py-1.5 text-muted">{t.reason || t.reference_id || "—"}</td>
              <td className="px-3 py-1.5">{statusLabel(t.status)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DepositTable({
  rows,
  loading,
  onAction,
}: {
  rows: any[];
  loading: boolean;
  onAction: (verb: RowVerb, depositID: string) => void;
}) {
  if (loading) return <div className="space-y-2 p-5">{[0, 1].map((i) => <Skeleton key={i} className="h-6" />)}</div>;
  if (rows.length === 0) return <p className="px-5 py-6 text-2xs text-muted">none</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-2xs">
        <thead className="border-b border-border bg-surface-2 text-muted">
          <tr>
            <th className="px-3 py-2 text-left">When</th>
            <th className="px-3 py-2 text-left">Provider</th>
            <th className="px-3 py-2 text-right">Amount</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-left">Reason</th>
            <th className="px-3 py-2 w-8" aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => {
            const adminTouched = isAdminReason(d.reason);
            return (
              <tr key={d.deposit_id} className="border-b border-border/60 last:border-0">
                <td className="px-3 py-1.5 text-muted">{fmtRelative(d.created_at)}</td>
                <td className="px-3 py-1.5">{d.provider || "—"}</td>
                <td className="px-3 py-1.5 text-right font-mono"><SparkAmount value={d.amount} /></td>
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-1.5">
                    <Badge tone={d.status === "COMPLETED" ? "success" : d.status === "FAILED" ? "danger" : "muted"}>
                      {statusLabel(d.status)}
                    </Badge>
                    {adminTouched && <Badge tone="muted">ADMIN</Badge>}
                  </div>
                </td>
                <td className="max-w-[20ch] truncate px-3 py-1.5 text-muted" title={d.reason || ""}>
                  {d.reason || "—"}
                </td>
                <td className="px-3 py-1.5 text-right">
                  {isCryptoRow(d.provider) ? (
                    <WalletOpsLink tab="deposits" />
                  ) : (
                    d.status === "PENDING" && (
                      <RowActionsMenu
                        items={[
                          { label: "Mark completed", onClick: () => onAction("deposit:complete", d.deposit_id) },
                          { label: "Mark failed", onClick: () => onAction("deposit:fail", d.deposit_id), tone: "danger" },
                        ]}
                      />
                    )
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function WithdrawalTable({
  rows,
  loading,
  onAction,
}: {
  rows: any[];
  loading: boolean;
  onAction: (verb: RowVerb, withdrawalID: string) => void;
}) {
  if (loading) return <div className="space-y-2 p-5">{[0, 1].map((i) => <Skeleton key={i} className="h-6" />)}</div>;
  if (rows.length === 0) return <p className="px-5 py-6 text-2xs text-muted">none</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-2xs">
        <thead className="border-b border-border bg-surface-2 text-muted">
          <tr>
            <th className="px-3 py-2 text-left">When</th>
            <th className="px-3 py-2 text-left">Provider</th>
            <th className="px-3 py-2 text-right">Amount</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-left">Reason</th>
            <th className="px-3 py-2 w-8" aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((wd) => {
            const adminTouched = isAdminReason(wd.reason);
            const inHold = wd.status === "REQUESTED" || wd.status === "SENT";
            return (
              <tr key={wd.withdrawal_id} className="border-b border-border/60 last:border-0">
                <td className="px-3 py-1.5 text-muted">{fmtRelative(wd.created_at)}</td>
                <td className="px-3 py-1.5">{wd.provider || "—"}</td>
                <td className="px-3 py-1.5 text-right font-mono"><SparkAmount value={wd.amount} /></td>
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-1.5">
                    <Badge tone={wd.status === "COMPLETED" ? "success" : wd.status === "FAILED" ? "danger" : "muted"}>
                      {statusLabel(wd.status)}
                    </Badge>
                    {adminTouched && <Badge tone="muted">ADMIN</Badge>}
                  </div>
                </td>
                <td className="max-w-[20ch] truncate px-3 py-1.5 text-muted" title={wd.reason || ""}>
                  {wd.reason || "—"}
                </td>
                <td className="px-3 py-1.5 text-right">
                  {isCryptoRow(wd.provider) ? (
                    <WalletOpsLink tab="withdrawals" />
                  ) : (
                    inHold && (
                      <RowActionsMenu
                        items={[
                          { label: "Mark completed", onClick: () => onAction("withdrawal:complete", wd.withdrawal_id) },
                          { label: "Cancel (revert hold)", onClick: () => onAction("withdrawal:cancel", wd.withdrawal_id) },
                          { label: "Mark failed", onClick: () => onAction("withdrawal:fail", wd.withdrawal_id), tone: "danger" },
                        ]}
                      />
                    )
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// RowActionsMenu — three-dot kebab dropdown.
//
// The dropdown panel is rendered through a React portal to
// `document.body` so the surrounding card's `overflow-hidden` (used
// for rounded corners) and the table wrapper's `overflow-x-auto` (used
// for horizontal scroll on narrow screens) cannot clip it. Position is
// derived each open from the trigger button's bounding rect, then
// pinned with `position: fixed`.
//
// Closes on outside click, Escape, scroll, and resize — the latter two
// because a portaled `position: fixed` element does NOT track the
// trigger on its own, and a stale dropdown floating in the corner of
// the viewport is worse than a closed one.
function RowActionsMenu({
  items,
}: {
  items: { label: string; onClick: () => void; tone?: "default" | "danger" }[];
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Compute the portal position synchronously when the menu opens so
  // there's no first-frame flicker at (0, 0).
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      close();
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    // Any geometry change invalidates the cached position. Cheaper to
    // close than to keep recomputing on every wheel tick.
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Row actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="rounded px-2 py-1 text-muted hover:bg-surface-2 hover:text-fg"
      >
        ⋯
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: "fixed", top: pos.top, right: pos.right }}
          className="z-50 w-44 overflow-hidden rounded-md border border-line-1 bg-bg-2 py-1 shadow-card"
        >
          {items.map((item, i) => (
            <button
              key={i}
              role="menuitem"
              type="button"
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              className={
                "block w-full px-3 py-2 text-left text-2xs hover:bg-surface-2 " +
                (item.tone === "danger" ? "text-danger" : "text-fg")
              }
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}

// amountTone colors signed display_amount: positive (credit in) is
// success, negative (debit out) is muted — matching the user-side
// wallet history convention so operator + user views read the same.
function amountTone(s: string | undefined): string {
  if (!s) return "";
  const n = Number(s);
  if (!Number.isFinite(n) || n === 0) return "";
  return n > 0 ? "text-success" : "text-muted";
}

// isAdminReason detects the `[admin:<id>]` attribution prefix ledger
// stamps onto rows transitioned via an operator action.
function isAdminReason(reason: string | undefined): boolean {
  return !!reason && reason.startsWith("[admin:");
}

// isCryptoRow — rows the crypto rail owns. Their lifecycle lives in
// crypto-wallet-service (confirmations / signing / broadcast); the
// mock-era manual verbs must never touch them.
function isCryptoRow(provider: string | undefined): boolean {
  return (provider || "").toUpperCase() === "CRYPTO";
}

// WalletOpsLink — routes the operator to the actual wallet procedures
// for crypto rows instead of the manual kebab. SM-267 split the old
// /wallet-ops/orders tab bar into per-flow routes.
function WalletOpsLink({ tab }: { tab: "deposits" | "withdrawals" }) {
  return (
    <Link
      to={tab === "deposits" ? "/wallet-ops/deposits" : "/wallet-ops/withdrawals"}
      title="Crypto rows follow the on-chain lifecycle — manage them in Crypto Wallets"
      className="whitespace-nowrap text-2xs text-muted underline decoration-dotted underline-offset-2 hover:text-fg"
    >
      Crypto rail ↗
    </Link>
  );
}

// --- row-verb metadata (single source of truth for modal copy) ---

function rowVerbTitle(v: RowVerb): string {
  switch (v) {
    case "deposit:complete":
      return "Mark deposit as completed?";
    case "deposit:fail":
      return "Mark deposit as failed?";
    case "withdrawal:complete":
      return "Mark withdrawal as completed?";
    case "withdrawal:cancel":
      return "Cancel withdrawal?";
    case "withdrawal:fail":
      return "Mark withdrawal as failed?";
  }
}

function rowVerbConfirm(v: RowVerb): string {
  switch (v) {
    case "deposit:complete":
    case "withdrawal:complete":
      return "Mark completed";
    case "deposit:fail":
    case "withdrawal:fail":
      return "Mark failed";
    case "withdrawal:cancel":
      return "Cancel withdrawal";
  }
}

function rowVerbTone(v: RowVerb): "primary" | "danger" {
  switch (v) {
    case "deposit:complete":
    case "withdrawal:complete":
      return "primary";
    case "deposit:fail":
    case "withdrawal:fail":
    case "withdrawal:cancel":
      return "danger";
  }
}

// Completion paths can carry an empty reason (the audit log still gets
// the [admin:<id>] tag). Failure / cancel paths require a written
// justification so the wallet owner has context if they ask.
function rowVerbReasonRequired(v: RowVerb): boolean {
  switch (v) {
    case "deposit:complete":
    case "withdrawal:complete":
      return false;
    case "deposit:fail":
    case "withdrawal:cancel":
    case "withdrawal:fail":
      return true;
  }
}
